import { useCallback, useRef } from 'react';

import {
  readSearchCriteriaUrlStateFromAsPath,
  appendSearchCriteriaParams,
} from '../searchCriteria/urlState';
import { useSearchCriteria } from '../searchCriteria/useSearchCriteria';
import {
  resolveSearchListParamNames,
  readSearchListUrlStateFromAsPath,
  appendSearchListParams,
} from '../searchListState/urlState';
import { useSearchListState } from '../searchListState/useSearchListState';
import { serializeCriteria, serializeListUrlState } from '../serialization';
import { defaultQueryParamName } from '../sharedConstants';
import type { SearchPageCriteria } from '../sharedTypes';
import { useSearchPageUrlSync } from '../urlState';
import { useSearchFacetsState, type SearchFacetsFetchResult } from '../useSearchFacetsState';
import { useSearchPageCriteriaController } from '../useSearchPageCriteriaController';
import type { UseSingleListSearchPageOptions, UseSingleListSearchPageResult } from './types';

const defaultSingleListKey = 'default';

type SingleListUrlState<FilterName extends string, SortValue extends string> = {
  criteria: SearchPageCriteria<FilterName>;
  list: {
    take: number;
    sort: SortValue | null;
  };
};

export const useSingleListSearchPage = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  options: UseSingleListSearchPageOptions<Item, FilterName, SortValue, Cursor>
): UseSingleListSearchPageResult<Item, FilterName, SortValue, Cursor> => {
  const queryParamName = options.queryParamName ?? defaultQueryParamName;
  const { takeParamName, sortParamName } = resolveSearchListParamNames({
    listKey: defaultSingleListKey,
    namespaced: false,
    takeParamName: options.takeParamName,
    sortParamName: options.sortParamName,
  });
  const criteriaState = useSearchCriteria({
    filters: options.filters,
  });
  const criteriaRef = useRef<SearchPageCriteria<FilterName>>(criteriaState.appliedCriteria);
  const currentUrlStateRef = useRef<SingleListUrlState<FilterName, SortValue>>({
    criteria: criteriaState.appliedCriteria,
    list: {
      take: options.pageSize,
      sort: options.defaultSort ?? null,
    },
  });
  const applyUrlStateRef = useRef<(urlState: SingleListUrlState<FilterName, SortValue>) => void>(
    () => {}
  );

  const readUrlStateFromAsPath = useCallback(
    (asPath: string) => {
      const nextCriteriaUrlState = readSearchCriteriaUrlStateFromAsPath({
        asPath,
        filterDefinitions: options.filters,
        filterNames: criteriaState.filterNames,
        queryParamName,
      });
      const nextListUrlState = readSearchListUrlStateFromAsPath({
        asPath,
        pageSize: options.pageSize,
        takeParamName,
        sortParamName,
        sortOptions: options.sortOptions,
        defaultSort: options.defaultSort,
      });

      return {
        criteria: nextCriteriaUrlState,
        list: nextListUrlState,
      };
    },
    [
      criteriaState.filterNames,
      options.defaultSort,
      options.filters,
      options.pageSize,
      options.sortOptions,
      queryParamName,
      sortParamName,
      takeParamName,
    ]
  );

  const serializeUrlState = useCallback(
    (urlState: SingleListUrlState<FilterName, SortValue>) =>
      JSON.stringify({
        criteria: JSON.parse(serializeCriteria(urlState.criteria, criteriaState.filterNames)),
        list: JSON.parse(serializeListUrlState(urlState.list)),
      }),
    [criteriaState.filterNames]
  );

  const buildSearchParams = useCallback(
    (urlState: SingleListUrlState<FilterName, SortValue>) => {
      const params = new URLSearchParams();

      appendSearchCriteriaParams({
        params,
        urlState: urlState.criteria,
        filterDefinitions: options.filters,
        filterNames: criteriaState.filterNames,
        queryParamName,
      });
      appendSearchListParams({
        params,
        urlState: urlState.list,
        pageSize: options.pageSize,
        takeParamName,
        sortParamName,
        defaultSort: options.defaultSort,
      });

      return params;
    },
    [
      criteriaState.filterNames,
      options.defaultSort,
      options.filters,
      options.pageSize,
      queryParamName,
      sortParamName,
      takeParamName,
    ]
  );

  const { hasHydratedFromUrl, setNavigationMode } = useSearchPageUrlSync({
    readUrlStateFromAsPath,
    applyUrlState: (urlState) => applyUrlStateRef.current(urlState),
    getCurrentUrlState: () => currentUrlStateRef.current,
    serializeUrlState,
    buildSearchParams,
  });

  const serializedCriteria = serializeCriteria(
    criteriaState.appliedCriteria,
    criteriaState.filterNames
  );

  const listState = useSearchListState<
    Item,
    FilterName,
    typeof defaultSingleListKey,
    SortValue,
    Cursor
  >({
    enabled: hasHydratedFromUrl,
    listKey: defaultSingleListKey,
    criteria: criteriaState.appliedCriteria,
    serializedCriteria,
    pageSize: options.pageSize,
    fetcher: async ({ listKey: _listKey, ...fetchArgs }) => options.fetcher(fetchArgs),
    sortOptions: options.sortOptions,
    defaultSort: options.defaultSort,
    // `initialFacetState` is deliberately NOT threaded here: the seed is redirected to the
    // criteria-level facets hook below; the list-level seed surface is deprecated and
    // slated for removal.
  });

  const facetsFetcher = options.facetsFetcher;
  const facetsState = useSearchFacetsState<FilterName>({
    // Matches the list-execution convention; pre-hydration criteria are empty and would
    // resolve to the seed anyway, but stating the gate removes reliance on that invariant.
    // Without a fetcher the facets state is inert (never fetches).
    enabled: hasHydratedFromUrl && facetsFetcher !== undefined,
    serializedCriteria,
    criteria: criteriaState.appliedCriteria,
    fetcher: facetsFetcher ?? (async (): Promise<SearchFacetsFetchResult<FilterName>> => ({})),
    initialFacetState: options.initialFacetState,
  });

  criteriaRef.current = criteriaState.appliedCriteria;
  currentUrlStateRef.current = {
    criteria: criteriaRef.current,
    list: {
      take: listState.take,
      sort: listState.sort,
    },
  };
  applyUrlStateRef.current = (urlState) => {
    criteriaState.applyUrlState(urlState.criteria);
    listState.applyUrlState(urlState.list);
  };

  const resetVisibleResultCount = (_nextCriteria?: SearchPageCriteria<FilterName>) => {
    listState.resetTake();
  };
  const criteriaController = useSearchPageCriteriaController({
    criteriaState,
    filterDefinitions: options.filters,
    getCurrentCriteriaState: () => ({
      queryDraft: criteriaState.queryDraft,
      query: criteriaState.query,
      selectedFilters: criteriaState.selectedFilters,
    }),
    setNavigationMode,
    onCriteriaChange: resetVisibleResultCount,
    onClearAll: () => {
      resetVisibleResultCount();
      listState.resetSort();
    },
  });

  const setSort = (nextSort: SortValue | null) => {
    setNavigationMode('replace');
    resetVisibleResultCount();
    listState.setSort(nextSort);
  };

  return {
    items: listState.items,
    filters: criteriaController.filters,
    facetState: facetsState.facetState,
    isFetching: facetsState.isFetching,
    searchList: listState.searchList,
    query: criteriaState.query,
    queryDraft: criteriaState.queryDraft,
    setQueryDraft: criteriaState.setQueryDraft,
    submitQuery: criteriaController.submitQuery,
    clearQuery: criteriaController.clearQuery,
    sort: listState.sort,
    setSort,
    sortOptions: listState.sortOptions,
    loading: listState.loading,
    hasLoaded: listState.hasLoaded,
    error: listState.error,
    hasMore: listState.hasMore,
    loadMore: () => {
      setNavigationMode('replace');
      listState.loadMore();
    },
    totalCount: listState.totalCount,
    activeFilters: criteriaController.activeFilters,
    hasActiveFilters: criteriaController.hasActiveFilters,
    clearFilter: criteriaController.clearFilter,
    clearAllFilters: criteriaController.clearAllFilters,
    clearAll: criteriaController.clearAll,
  };
};
